import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import ModalComponent from "../../../components/Modal";
import CustomDropdown from "../../../components/CustomDropdown";
import TextInput from "../../../components/TextInput";
import { documentSchema } from "../../../components/Helpers";

import moment from "moment";
import { useParams } from "react-router-dom";

import MuiDatePicker from "../../../components/MuiDatePicker";

const DocumentModal = ({
  onClose,
  show,
  title,
  initialValues,
  documentsList,
  submitForm,
}) => {
  
  const [list, setList] = useState([]);
  const { id } = useParams();
 
  const [isOthersSelected, setIsOthersSelected] = useState(false);
  const [shouldShowDates, setShouldShowDates] = useState(true);

  const handleSubmit = (values) => {
    // If OTHER is selected, use the others input value as the document type
    const documentType = values.documentType === "OTHER" ? values.others : values.documentType;
    
    // Create a new values object with the updated document type
    const updatedValues = {
      ...values,
      documentType: documentType
    };
    
    submitForm(updatedValues);
    handleClose();
  };

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    setList(() => {
      return documentsList.map((item) => {
        return {
          label: item.label,
          value: item.value,
        };
      });
    });
  }, [documentsList]);

  // Set initial states based on initialValues
  useEffect(() => {
    if (initialValues?.documentType) {
      setIsOthersSelected(initialValues.documentType === "OTHER");
      setShouldShowDates(
        initialValues.documentType !== "Resume" && 
        initialValues.documentType !== "OTHER"
      );
    }
  }, [initialValues]);

  return (
    <ModalComponent show={show} onClose={handleClose} title={title}>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
        validationSchema={documentSchema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <Form className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 py-3">
            {/* Document Type */}
            <div className="space-y-2">
              <CustomDropdown
                value={
                  list?.find((user) => user.value === values?.documentType) ||
                  null
                }
                name="documentType"
                handleChange={(selectedOption) => {
                  setFieldValue("documentType", selectedOption.value);
                  setIsOthersSelected(selectedOption.value === "OTHER");
                  
                  // Hide dates for RESUME or OTHER document types
                  const shouldHideDates = 
                    selectedOption.value === "Resume" || 
                    selectedOption.value === "OTHER";
                  
                  setShouldShowDates(!shouldHideDates);
                  
                  // Clear date values if hiding date fields
                  if (shouldHideDates) {
                    setFieldValue("issueDate", null);
                    setFieldValue("expiryDate", null);
                  }
                }}
                customClasses="w-full text-left"
                placeholder="Select Type of Document"
                options={list}
                handleGetOptionValue={(option) => option.value}
                handleGetOptionName={(option) => option.label}
              />
              {errors.documentType && touched.documentType && (
                <div className="text-red-500 text-sm">
                  {errors.documentType}
                </div>
              )}

              {isOthersSelected && (
                <div className="space-y-2 pt-3">
                  <TextInput
                    type="text"
                    name="others"
                    handleChange={handleChange}
                    value={values.others}
                    placeholder="Enter Document Type"
                    disabled={!isOthersSelected}
                    classes="w-full"
                  />
                  {errors.others && touched.others && (
                    <div className="text-red-500 text-sm">{errors.others}</div>
                  )}
                </div>
              )}
            </div>

            {/* Upload Document */}
            <div className="space-y-2">
              <input
                type="file"
                id="file"
                name="file"
                onChange={(event) => {
                  const file = event.target.files[0];
                  setFieldValue("file", file);
                }}
                className="block w-full px-3 py-1 border border-gray-300 h-[2.5rem] rounded-md focus:ring-1 focus:ring-primary"
              />
              {errors.file && touched.file && (
                <div className="text-red-500 text-sm">{errors.file}</div>
              )}
            </div>
            
            {/* Issue Date - Only show if not RESUME or OTHER */}
            {shouldShowDates && (
              <div className="space-y-2 relative overflow-visible">
                <MuiDatePicker
                  label="Issue Date"
                  value={values?.issueDate ? moment(values.issueDate) : null}
                  onChange={(date) => {
                    if (date) {
                      setFieldValue("issueDate", date.format("MM-DD-YYYY"));
                    }
                  }}
                />
                {errors.issueDate && touched.issueDate && (
                  <div className="text-red-500 text-sm">{errors.issueDate}</div>
                )}
              </div>
            )}

            {/* Expiry Date - Only show if not RESUME or OTHER */}
            {shouldShowDates && (
              <div className="space-y-2 relative overflow-visible">
                <MuiDatePicker
                  label="Expiry Date"
                  value={values?.expiryDate ? moment(values.expiryDate) : null}
                  onChange={(date) => {
                    if (date) {
                      setFieldValue("expiryDate", date.format("MM-DD-YYYY"));
                    }
                  }}
                  minDate={moment(values?.issueDate) || null}
                />
                {errors.expiryDate && touched.expiryDate && (
                  <div className="text-red-500 text-sm">{errors.expiryDate}</div>
                )}
              </div>
            )}
            
            <div className="flex items-center space-x-4">
              <label className="text-gray-700 text-sm cursor-default">
                Status:
              </label>
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <Field
                    type="radio"
                    name="statusCode"
                    value="A"
                    checked={values.statusCode === "A"}
                    onChange={handleChange}
                  />
                  <span className="text-sm">Active</span>
                </label>
                <label className="flex items-center space-x-2">
                  <Field
                    type="radio"
                    name="statusCode"
                    value="D"
                    checked={values.statusCode === "D"}
                    onChange={handleChange}
                  />
                  <span className="text-sm">Deactive</span>
                </label>
              </div>
              {errors.statusCode && touched.statusCode && (
                <div className="text-red-500 text-sm">{errors.statusCode}</div>
              )}
            </div>

            <div className="col-span-1 sm:col-span-2 flex flex-col sm:flex-row justify-end gap-2 mt-4">
              <button
                type="button"
                className="bg-gray-300 text-black h-10 py-1 px-4 w-full sm:w-40 rounded-lg hover:bg-gray-400"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-primary text-white h-10 py-1 px-4 w-full sm:w-40 rounded-lg hover:bg-primaryDark"
              >
                Submit
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </ModalComponent>
  );
};

export default DocumentModal;